import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { Avatar } from '@mui/material';
import dayjs from 'dayjs';
import { fonts } from '@theme/fontsCustomer';
import { numberFormat } from '@utils/numberFormat';
import type { SubscriptionBillingModel } from '@models/SubscriptionBillingModel';
import { TenantMonthlyBillingModel } from '@repositories/tenantMonthlyBillingRepository/models/TenantMonthlyBillingModel';

interface Props {
  billing: SubscriptionBillingModel | TenantMonthlyBillingModel;
}

export const Payment = ({ billing }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tenantId } = useParams();

  const handleClick = () => {
    if (billing instanceof TenantMonthlyBillingModel)
      navigate(`/manager/${tenantId}/billings/history/tenant/${billing.month}`);
    else navigate(`/manager/${tenantId}/subscriptions/list/${billing.subscriptionId}/billing`);
  };

  return (
    <Container onClick={handleClick}>
      <PaymentMetaInfo>
        <span className="info">
          {billing instanceof TenantMonthlyBillingModel ? (
            <Avatar sx={{ width: '16px', height: '16px', borderRadius: '3px' }} variant="square">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="20" height="20" rx="3" fill="#6F42C1" />
                <rect x="0.5" y="0.5" width="19" height="19" rx="2.5" stroke="black" strokeOpacity="0.1" />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M5.19995 13.7976C5.19995 14.3512 5.64873 14.8 6.20233 14.8H7.98805C8.21291 14.8 8.39519 14.6177 8.39519 14.3928V13.3714H9.72376V14.3928C9.72376 14.6177 9.90604 14.8 10.1309 14.8H11.9166C11.9598 14.8 12.0024 14.7972 12.0441 14.7919C12.0703 14.7972 12.0973 14.7999 12.125 14.7999H14.0596C14.6133 14.7999 15.0619 14.351 15.0619 13.7976V10.1666C15.0619 9.8511 14.9133 9.55401 14.6609 9.36471L14.1847 9.00756C14.0048 8.87265 13.7496 8.9091 13.6147 9.08899C13.4798 9.26888 13.5163 9.52408 13.6961 9.65899L14.1723 10.0161C14.2197 10.0517 14.2476 10.1074 14.2476 10.1666V13.7976C14.2476 13.9016 14.1634 13.9857 14.0596 13.9857H12.9014C12.9129 13.9247 12.919 13.8618 12.919 13.7976V5.70233C12.919 5.14873 12.4702 4.69995 11.9166 4.69995H6.20233C5.64873 4.69995 5.19995 5.14873 5.19995 5.70233V13.7976ZM6.91663 7.01193C6.91663 6.81468 7.07652 6.65479 7.27377 6.65479H7.51186C7.70911 6.65479 7.86901 6.81468 7.86901 7.01193C7.86901 7.20917 7.70911 7.36907 7.51186 7.36907H7.27377C7.07652 7.36907 6.91663 7.20917 6.91663 7.01193ZM6.91663 8.67859C6.91663 8.48135 7.07652 8.32145 7.27377 8.32145H7.51186C7.70911 8.32145 7.86901 8.48135 7.86901 8.67859C7.86901 8.87584 7.70911 9.03574 7.51186 9.03574H7.27377C7.07652 9.03574 6.91663 8.87584 6.91663 8.67859ZM7.27377 9.98812C7.07652 9.98812 6.91663 10.148 6.91663 10.3453C6.91663 10.5425 7.07652 10.7024 7.27377 10.7024H7.51186C7.70911 10.7024 7.86901 10.5425 7.86901 10.3453C7.86901 10.148 7.70911 9.98812 7.51186 9.98812H7.27377ZM8.58329 10.3453C8.58329 10.148 8.74319 9.98812 8.94044 9.98812H9.17853C9.37577 9.98812 9.53567 10.148 9.53567 10.3453C9.53567 10.5425 9.37577 10.7024 9.17853 10.7024H8.94044C8.74319 10.7024 8.58329 10.5425 8.58329 10.3453ZM8.58329 8.67859C8.58329 8.48135 8.74319 8.32145 8.94044 8.32145H9.17853C9.37577 8.32145 9.53567 8.48135 9.53567 8.67859C9.53567 8.87584 9.37577 9.03574 9.17853 9.03574H8.94044C8.74319 9.03574 8.58329 8.87584 8.58329 8.67859ZM8.58329 7.01193C8.58329 6.81468 8.74319 6.65479 8.94044 6.65479H9.17853C9.37577 6.65479 9.53567 6.81468 9.53567 7.01193C9.53567 7.20917 9.37577 7.36907 9.17853 7.36907H8.94044C8.74319 7.36907 8.58329 7.20917 8.58329 7.01193ZM10.25 10.3453C10.25 10.148 10.4099 9.98812 10.6071 9.98812H10.8452C11.0424 9.98812 11.2023 10.148 11.2023 10.3453C11.2023 10.5425 11.0424 10.7024 10.8452 10.7024H10.6071C10.4099 10.7024 10.25 10.5425 10.25 10.3453ZM10.25 8.67859C10.25 8.48135 10.4099 8.32145 10.6071 8.32145H10.8452C11.0424 8.32145 11.2023 8.48135 11.2023 8.67859C11.2023 8.87584 11.0424 9.03574 10.8452 9.03574H10.6071C10.4099 9.03574 10.25 8.87584 10.25 8.67859ZM10.25 7.01193C10.25 6.81468 10.4099 6.65479 10.6071 6.65479H10.8452C11.0424 6.65479 11.2023 6.81468 11.2023 7.01193C11.2023 7.20917 11.0424 7.36907 10.8452 7.36907H10.6071C10.4099 7.36907 10.25 7.20917 10.25 7.01193Z"
                  fill="white"
                />
                <rect x="13.53" y="9.30005" width="1" height="4.8" fill="white" />
              </svg>
            </Avatar>
          ) : (
            <Avatar
              sx={{ width: '20px', height: '20px' }}
              variant="square"
              src={billing.subscription.software.iconUrl}
            />
          )}
          <span className="name">
            {billing instanceof TenantMonthlyBillingModel ? t('Acc_Main_26') : billing.subscription.name}
          </span>
        </span>
        <span className="date">
          <span className="line" />
          {dayjs(billing.paymentDate).format(t('DateFormat_YMD'))}
        </span>
      </PaymentMetaInfo>
      <Cost>{`(${billing.currencyUnit})${numberFormat({
        num: billing.cost,
        currency: billing.currencyUnit,
      })}`}</Cost>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 72px;
  justify-content: center;
  padding: 0px 16px;
  gap: 6px;
  border: 0.5px solid ${props => props.theme.colors['border-gray-lighter']};
  border-radius: 7px;
  :hover {
    cursor: pointer;
    background: ${props => props.theme.colors['state-white-primary-hover']};
    border: 0.5px solid ${props => props.theme.colors['border-primary-light']};
  }
  box-shadow: 0px 1px 0px rgba(0, 0, 0, 0.02);
`;

const PaymentMetaInfo = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  & .info {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;
    ${fonts.Headline8};
    max-width: 140px;
    color: ${props => props.theme.colors['text-gray-main']};
    & .name {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      word-break: break-all;
      width: 124px;
    }
  }
  & .date {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: right;
    ${fonts.Body4};
    color: ${props => props.theme.colors['text-gray-light']};
    width: 95px;
    & .line {
      width: 1px;
      height: 9px;
      background: ${props => props.theme.colors['border-gray-light']};
      margin-right: 10px;
    }
  }
`;

const Cost = styled.div`
  ${fonts.Body1};
  color: ${props => props.theme.colors['text-gray-main']};
`;
